import {
    createCommandProcessor,
    UWM_CLOSE_POPUPS,
    UWM_HOLDER_CONNECTED,
    UWM_INITIALIZE,
    UWM_OPEN_MODAL,
    UWM_THEME_CHANGE,
    UWM_TOKEN_CHANGE,
} from '@uwm/host';
import {MODAL_TYPES} from '@/consts/modal/modalTypes';
import {UWM_INIT_EXTERNAL_LINKS} from '@/consts/uwmEventTypes';
import mockDataMode from '@/mocks/mockDataMode';
import {initContent} from '@/services/content/content';
import {initLocalization} from '@/services/localization/localizationService';
import modalService from '@/services/modal/modalService';
import uamfInferenceService from '@/services/uamf/uamfInferenceService';
import uwmRequestService from '@/services/uwm/uwmRequestService';
import {addSsoLink, clearSsoLinks, type SsoLinkStatus} from '@/state/ducks/externalLinks';
import {clearInferenceData} from '@/state/ducks/inference';
import {setInsightsContext} from '@/state/ducks/insights';
import {resetState, setContentLoaded, setDarkMode, setInitialData, setModuleDetails} from '@/state/ducks/uwm';
import {dispatch} from '@/state/store';
import type {InferenceApiResponse} from '@/types/inference';

export const EC_INFERENCE = 'ec_inference';

const loadContent = async (marketName: string, languageCode: string) => {
    await initContent(marketName, languageCode);
    dispatch(setContentLoaded(true));
};

let initPayload: Record<string, unknown> = {};

const initHandler = (update: Record<string, unknown>) => {
    initPayload = {...initPayload, ...update};
    const payload = initPayload;
    const countryCode = payload.countryCode as string;
    const languageCode = (payload.languageCode as string).toLowerCase();

    mockDataMode.apply({
        useMockData: payload.useMockData as boolean | undefined,
    });

    dispatch(resetState());
    dispatch(clearInferenceData());
    modalService.reset();
    dispatch(clearSsoLinks());
    dispatch(
        setInitialData({
            countryCode,
            languageCode,
            isDarkMode: (payload.isDarkMode as boolean) ?? false,
            assetId: (payload.assetId as string) ?? '',
            topInset: (payload.paddingTop as number) ?? 0,
            menuInset: (payload.paddingBottomMenu as number) ?? 0,
        })
    );
    dispatch(
        setInsightsContext({
            assetId: (payload.assetId as string) ?? '',
            dusn: (payload.dusn as string) ?? '',
            uamfApiUrl: (payload.uamfApiUrl as string) ?? '',
            uamToken: (payload.uamToken as string) ?? '',
            uamfApiKey: (payload.uamfApiKey as string) ?? '',
            isRetail: (payload.isRetail as boolean) ?? false,
            isRangeModeEnabled: (payload.isRangeModeEnabled as boolean) ?? false,
        })
    );

    dispatch(setModuleDetails({isInitialized: true}));
    initLocalization(languageCode);
    void loadContent(countryCode, languageCode);
    uamfInferenceService.handleExperienceInference();
};

const themeChangeHandler = (payload: Record<string, unknown>) => {
    dispatch(setDarkMode(payload.isDarkMode as boolean));
};

// Reinserting the holder syncs a new batch of records, so bypass the inference freshness window.
const holderConnectedHandler = () => {
    uamfInferenceService.handleExperienceInference({isForced: true});
};

const closePopupsHandler = () => {
    modalService.closeAll();
};

const openModalHandler = (payload: Record<string, unknown>) => {
    const modalType = payload.modalType as string;
    const modalProps = (payload.modalProps as Record<string, unknown>) ?? {};

    if (modalType === MODAL_TYPES.EXPERIENCE_SCORE_DETAILED_INSIGHT_MODAL) {
        const insightId = modalProps.insightId as string | undefined;

        if (insightId) modalService.openDetail(insightId);
    } else if (modalType === MODAL_TYPES.EXPERIENCE_SCORE_INFO_MODAL) {
        modalService.openInfo(modalProps.code as string | undefined);
    }
};

interface ExternalLinkResult {
    link?: string;
    expirationDate?: number;
    status?: SsoLinkStatus;
}

interface ExternalLinkEntry {
    template?: string;
    externalLinkResult?: ExternalLinkResult;
}

const initExternalLinksHandler = (payload: Record<string, unknown>) => {
    const externalLinks = payload.externalLinks as ExternalLinkEntry[] | undefined;
    if (!externalLinks?.length) return;

    externalLinks.forEach((entry) => {
        const {template, externalLinkResult} = entry ?? {};
        if (!template || !externalLinkResult) return;
        dispatch(
            addSsoLink({
                template,
                link: externalLinkResult.link,
                expirationDate: externalLinkResult.expirationDate,
                status: externalLinkResult.status ?? 'success',
            })
        );
    });
};

const messageHandlers = {
    [UWM_INITIALIZE]: initHandler,
    [UWM_THEME_CHANGE]: themeChangeHandler,
    [UWM_TOKEN_CHANGE]: uwmRequestService.handleResponse,
    [UWM_CLOSE_POPUPS]: closePopupsHandler,
    [UWM_OPEN_MODAL]: openModalHandler,
    [UWM_HOLDER_CONNECTED]: holderConnectedHandler,
    [UWM_INIT_EXTERNAL_LINKS]: initExternalLinksHandler,
    [EC_INFERENCE]: (payload: Record<string, unknown>) =>
        uamfInferenceService.applyInference(payload as unknown as InferenceApiResponse),
};

export const processUwmCommand = createCommandProcessor(messageHandlers);
